
import React, { useEffect, useState, useCallback } from 'react';

// Progress loader component
const ProgressLoader = ({ progress, isLoading }) => {
    return (
        <div className="flex flex-col items-center justify-center p-8 bg-gray-50 dark:bg-gray-800 rounded-lg">
            <div className="w-full max-w-md">
                <div className="flex justify-between text-sm text-gray-600 dark:text-gray-400 mb-2">
                    <span>Loading Report...</span>
                    <span>{Math.round(progress)}%</span>
                </div>
                <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                    <div
                        className="bg-indigo-600 h-2 rounded-full transition-all duration-300 ease-out"
                        style={{ width: `${progress}%` }}
                    ></div>
                </div>
                <div className="mt-4 text-center">
                    <div className="inline-flex items-center">
                        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-indigo-600 mr-2"></div>
                        <span className="text-sm text-gray-600 dark:text-gray-400">
                            {isLoading ? 'Processing data...' : 'Complete!'}
                        </span>
                    </div>
                </div>
            </div>
        </div>
    );
};

// Optimized HTML renderer with progress tracking using iframe for proper script execution
const HtmlRenderer = ({ filename, onLoadComplete }) => {
    const [isLoading, setIsLoading] = useState(true);
    const [progress, setProgress] = useState(0);
    const [error, setError] = useState(null);

    const simulateProgress = useCallback(() => {
        let currentProgress = 0;
        const interval = setInterval(() => {
            currentProgress += Math.random() * 15;
            if (currentProgress >= 90) {
                currentProgress = 90;
                clearInterval(interval);
            }
            setProgress(currentProgress);
        }, 100);
        return interval;
    }, []);

    useEffect(() => {
        const progressInterval = simulateProgress();

        // Simulate loading progress
        const timeout = setTimeout(() => {
            clearInterval(progressInterval);
            setProgress(100);
            // Allow iframe to load naturally
        }, 2000);

        return () => {
            clearInterval(progressInterval);
            clearTimeout(timeout);
        };
    }, [simulateProgress]);

    const handleIframeLoad = useCallback(() => {
        setIsLoading(false);
        setProgress(100);
        onLoadComplete?.();
    }, [onLoadComplete]);

    const handleIframeError = useCallback(() => {
        setError('Failed to load HTML content');
        setIsLoading(false);
        setProgress(0);
    }, []);

    if (error) {
        return (
            <div className="flex items-center justify-center p-8 bg-red-50 dark:bg-red-900/20 rounded-lg">
                <div className="text-center">
                    <div className="text-red-600 dark:text-red-400 text-lg font-medium mb-2">Failed to load content</div>
                    <div className="text-red-500 dark:text-red-300 text-sm">{error}</div>
                </div>
            </div>
        );
    }

    return (
        <div className="w-full relative">
            {isLoading && (
                <div className="absolute inset-0 z-10 bg-white dark:bg-gray-800 rounded-lg">
                    <ProgressLoader progress={progress} isLoading={isLoading} />
                </div>
            )}
            <iframe
                src={`${process.env.PUBLIC_URL}/${filename}`}
                className="w-full border-0 rounded-lg"
                style={{
                    minHeight: '800px',
                    visibility: isLoading ? 'hidden' : 'visible'
                }}
                title={`${filename} Report`}
                onLoad={handleIframeLoad}
                onError={handleIframeError}
                sandbox="allow-scripts allow-same-origin allow-popups allow-forms"
            />
        </div>
    );
};

export default function MmmTabs() {
    // State to manage the active tab. 'tab1' is active by default.
    const [activeTab, setActiveTab] = useState('tab1');
    const [loadedTabs, setLoadedTabs] = useState(new Set());

    const handleTabLoadComplete = useCallback((tabName) => {
        setLoadedTabs(prev => new Set([...prev, tabName]));
    }, []);

    return (
        // Main container for the tab view, now aligned to top-left and full width
        <div className="flex flex-col w-full bg-gray-100 dark:bg-gray-900 p-4 font-sans">
            <div className="w-full bg-white dark:bg-gray-800 shadow-lg rounded-lg overflow-hidden">
                {/* Tab buttons container */}
                <div className="flex border-b border-gray-200 dark:border-gray-700">
                    {/* Tab 1 Button */}
                    <button
                        className={`py-3 px-6 text-center text-lg font-medium transition-colors duration-200 flex-1
                       ${activeTab === 'tab1'
                                ? 'text-indigo-600 border-b-2 border-indigo-600 bg-indigo-50 dark:bg-indigo-900/20'
                                : 'text-gray-600 dark:text-gray-400 hover:text-indigo-600 hover:bg-gray-50 dark:hover:bg-gray-700'
                            } focus:outline-none rounded-tl-lg`}
                        onClick={() => setActiveTab('tab1')}
                    >
                        <span className="flex items-center justify-center">
                            Modeling Report
                            {loadedTabs.has('tab1') && (
                                <svg className="w-4 h-4 ml-2 text-green-500" fill="currentColor" viewBox="0 0 20 20">
                                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                </svg>
                            )}
                        </span>
                    </button>

                    {/* Tab 2 Button */}
                    <button
                        className={`py-3 px-6 text-center text-lg font-medium transition-colors duration-200 flex-1
                       ${activeTab === 'tab2'
                                ? 'text-indigo-600 border-b-2 border-indigo-600 bg-indigo-50 dark:bg-indigo-900/20'
                                : 'text-gray-600 dark:text-gray-400 hover:text-indigo-600 hover:bg-gray-50 dark:hover:bg-gray-700'
                            } focus:outline-none rounded-tr-lg`}
                        onClick={() => setActiveTab('tab2')}
                    >
                        <span className="flex items-center justify-center">
                            Optimization Report
                            {loadedTabs.has('tab2') && (
                                <svg className="w-4 h-4 ml-2 text-green-500" fill="currentColor" viewBox="0 0 20 20">
                                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                </svg>
                            )}
                        </span>
                    </button>
                </div>

                {/* Tab Content Area */}
                <div className="p-6">
                    {/* Content for Tab 1 - Modeling Report */}
                    {activeTab === 'tab1' && (
                        <div className="text-gray-800 dark:text-gray-200">
                            <div className="border border-gray-200 dark:border-gray-600 rounded-lg overflow-hidden">
                                <HtmlRenderer
                                    filename="mmm_output1.html"
                                    onLoadComplete={() => handleTabLoadComplete('tab1')}
                                />
                            </div>
                        </div>
                    )}

                    {/* Content for Tab 2 - Optimization Report */}
                    {activeTab === 'tab2' && (
                        <div className="text-gray-800 dark:text-gray-200">
                            <div className="border border-gray-200 dark:border-gray-600 rounded-lg overflow-hidden">
                                <HtmlRenderer
                                    filename="mmm_output2.html"
                                    onLoadComplete={() => handleTabLoadComplete('tab2')}
                                />
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}
